import * as aiApi from '@/api/ai'
import * as studioApi from '@/api/studio'

const state = {
  agents: [],
  templates: [],
  runs: [],
  studioFonts: [],
  canvaImportJobs: [],
  currentTemplate: null,
  generated: null,
  suggestions: [],
  loading: false,
}

const getters = {
  agents: (state) => state.agents,
  templates: (state) => state.templates,
  runs: (state) => state.runs,
  studioFonts: (state) => state.studioFonts,
  canvaImportJobs: (state) => state.canvaImportJobs,
  currentTemplate: (state) => state.currentTemplate,
  generated: (state) => state.generated,
  suggestions: (state) => state.suggestions,
  loading: (state) => state.loading,
}

const actions = {
  async fetchAgents({ commit }) {
    commit('SET_LOADING', true)
    try {
      const { data } = await aiApi.listAgents()
      commit('SET_AGENTS', data.data.agents || [])
      return data.data.agents
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async createAgent({ dispatch }, payload) {
    await aiApi.createAgent(payload)
    return dispatch('fetchAgents')
  },

  async updateAgent({ dispatch }, { id, payload }) {
    await aiApi.updateAgent(id, payload)
    return dispatch('fetchAgents')
  },

  async deleteAgent({ dispatch }, id) {
    await aiApi.deleteAgent(id)
    return dispatch('fetchAgents')
  },

  async runAgent({ dispatch }, { id, payload }) {
    const { data } = await aiApi.runAgent(id, payload)
    await dispatch('fetchRuns')
    return data.data.run
  },

  async fetchRuns({ commit }) {
    const { data } = await aiApi.listRuns()
    commit('SET_RUNS', data.data.runs || [])
    return data.data.runs
  },

  async fetchTemplates({ commit }) {
    const { data } = await aiApi.listTemplates()
    commit('SET_TEMPLATES', data.data.templates || [])
    return data.data.templates
  },

  async fetchTemplate({ commit }, id) {
    const { data } = await aiApi.getTemplate(id)
    commit('SET_CURRENT_TEMPLATE', data.data.template)
    return data.data.template
  },

  async createTemplate(_, payload) {
    const { data } = await aiApi.createTemplate(payload)
    return data.data.template
  },

  async updateTemplate(_, { id, payload }) {
    const { data } = await aiApi.updateTemplate(id, payload)
    return data.data.template
  },

  async deleteTemplate({ dispatch }, id) {
    await aiApi.deleteTemplate(id)
    return dispatch('fetchTemplates')
  },

  async generateLayerText(_, payload) {
    const { data } = await aiApi.generateLayerText(payload)
    return data.data.text
  },

  async studioCreatePost(_, payload) {
    const { data } = await aiApi.studioCreatePost(payload)
    return data.data.post
  },

  async fetchPublicStudio({ rootGetters }, payload) {
    const designId = typeof payload === 'string' ? payload : payload.id
    const page = typeof payload === 'object' && payload ? payload.page : undefined
    const apiKey = (rootGetters['auth/user'] && rootGetters['auth/user'].api_key) || ''
    const { data } = await studioApi.getPublicStudio(designId, apiKey, page)
    return data.data.design
  },

  async fetchPublicStudioList({ rootGetters }) {
    const apiKey = (rootGetters['auth/user'] && rootGetters['auth/user'].api_key) || ''
    const { data } = await studioApi.listPublicStudio(apiKey)
    return data.data.designs || []
  },

  async importCanva({ dispatch }, payload) {
    const { data } = await aiApi.importCanva(payload)
    await dispatch('fetchCanvaImportJobs')
    return data.data.job
  },

  async fetchCanvaImportJobs({ commit }) {
    const { data } = await aiApi.listCanvaImportJobs()
    const jobs = data.data.jobs || []
    commit('SET_CANVA_IMPORT_JOBS', jobs)
    return jobs
  },

  async reportCanvaImport(_, payload) {
    const { data } = await aiApi.reportCanvaImport(payload)
    return data.data.report
  },

  async fetchStudioFonts({ commit }) {
    const { data } = await aiApi.listStudioFonts()
    const fonts = data.data.fonts || []
    commit('SET_STUDIO_FONTS', fonts)
    return fonts
  },

  async generateContent({ commit }, payload) {
    commit('SET_LOADING', true)
    try {
      const { data } = await aiApi.generateContent(payload)
      commit('SET_GENERATED', data.data.content)
      return data.data.content
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async suggestSchedule({ commit }, payload) {
    const { data } = await aiApi.suggestSchedule(payload)
    commit('SET_SUGGESTIONS', data.data.suggestions || [])
    return data.data.suggestions
  },

  async generateAndCreate(_, payload) {
    const { data } = await aiApi.generateAndCreate(payload)
    return data.data
  },
}

const mutations = {
  SET_AGENTS(state, agents) { state.agents = agents },
  SET_TEMPLATES(state, templates) { state.templates = templates },
  SET_RUNS(state, runs) { state.runs = runs },
  SET_STUDIO_FONTS(state, fonts) { state.studioFonts = fonts },
  SET_CANVA_IMPORT_JOBS(state, jobs) { state.canvaImportJobs = jobs },
  SET_CURRENT_TEMPLATE(state, template) { state.currentTemplate = template },
  SET_GENERATED(state, content) { state.generated = content },
  SET_SUGGESTIONS(state, suggestions) { state.suggestions = suggestions },
  SET_LOADING(state, loading) { state.loading = loading },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
